import React, { useState, useEffect } from "react";
import { XMarkIcon } from "@heroicons/react/24/solid";
import { FaEye, FaStar } from "react-icons/fa";
import { useOutletContext, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import api from "../../Api/Api";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const MostBook = () => {
  const { onAddToCart } = useOutletContext();
  const [services, setServices] = useState([]);
  const [selectedService, setSelectedService] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const res = await api.get("catelog/products/1");
        setServices(Array.isArray(res.data) ? res.data : []);
      } catch (error) {
        console.error("Failed to fetch services", error);
      }
    };
    fetchServices();
  }, []);

  const handleAddToCart = (service) => {
    onAddToCart(service);
    toast.success(`${service.name} added to cart`, { autoClose: 1200 });
  };

  return (
    <>
      <ToastContainer position="top-right" />

      <section className="bg-[#f7f8fa] py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-10 flex flex-col justify-between gap-5 border-b border-gray-200 pb-6 sm:mb-12 sm:flex-row sm:items-end">
            <div className="max-w-2xl">
              <p className="mb-3 text-[10px] font-bold uppercase tracking-[0.28em] text-[#AF7D23] sm:text-xs">
                Popular in your neighbourhood
              </p>
              <h2 className="text-3xl font-semibold tracking-[-0.03em] text-[#0D004C] sm:text-4xl">
                Services people come back for
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-gray-500 sm:text-base">
                Reliable professionals for the jobs that matter at home.
              </p>
            </div>
            <div className="flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.16em] text-gray-400">
              <span className="h-2 w-2 rounded-full bg-[#AF7D23]" />
              Book with confidence
            </div>
          </div>

          <div className="grid grid-cols-1 gap-5 sm:gap-6 md:grid-cols-3">
            {services.map((service, index) => {
              const salePrice = Number(service.salePrice || service.mrp || 0);
              const price = Number(service.price || 0);
              const offer =
                salePrice > price
                  ? Math.round(((salePrice - price) / salePrice) * 100)
                  : 0;

              // Rating fallback logic (e.g. 4.8, 4.9, 5.0)
              const rating = (4.8 + (index % 3) * 0.1).toFixed(1);

              return (
                <motion.div
                  key={service.id || index}
                  whileHover={{ y: -6 }}
                  transition={{ duration: 0.25 }}
                  className="group/card flex flex-col justify-between overflow-hidden rounded-2xl border border-gray-200/80 bg-white shadow-[0_5px_20px_rgba(13,0,76,0.04)] transition-all duration-500 hover:-translate-y-1 hover:border-[#0D004C]/20 hover:shadow-[0_18px_35px_rgba(13,0,76,0.11)]"
                >
                  <div className="relative aspect-[1.35/1] w-full overflow-hidden bg-gray-100">
                    <img
                      src={
                        service.image?.startsWith("http")
                          ? service.image
                          : `http://skishore.in/api/public/${service.image}`
                      }
                      alt={service.name}
                      className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover/card:scale-110"
                    />

                    <div className="absolute inset-0 bg-gradient-to-t from-[#07102e]/55 via-transparent to-transparent opacity-60 transition-opacity duration-500 group-hover/card:opacity-90" />

                    {/* Left Top Badge: Red Discount or Green Best Value */}
                    {offer > 0 ? (
                      <div className="absolute left-4 top-4 rounded-md bg-[#E53935] px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.12em] text-white shadow-lg">
                        {offer}% OFF
                      </div>
                    ) : index === 2 ? (
                      <div className="absolute left-4 top-4 rounded-md bg-[#0A4D3C] px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.12em] text-white shadow-lg">
                        Best Value
                      </div>
                    ) : null}
                  </div>

                  <div className="flex flex-grow flex-col justify-between p-5 sm:p-6">
                    <div>
                      <div className="mb-2 flex items-center justify-between gap-2">
                        <h3 className="line-clamp-1 flex-1 text-lg font-bold text-[#0D004C]">
                          {service.name}
                        </h3>
                        <div className="flex shrink-0 items-center gap-1 text-xs font-bold text-gray-700">
                          <FaStar className="text-[#FFB800] text-sm" />
                          <span>{rating}</span>
                        </div>
                      </div>

                      {/* Description */}
                      <p className="mb-6 line-clamp-2 text-xs font-normal leading-relaxed text-gray-500">
                        {service.description ||
                          "Comprehensive diagnostics and professional service by certified masters."}
                      </p>
                    </div>

                    {/* Price & Action Buttons */}
                    <div className="flex items-end justify-between gap-3 border-t border-gray-100 pt-4">
                      <div>
                        {salePrice > price && (
                          <div className="mb-0.5 text-xs font-medium text-gray-400 line-through">
                            ₹{salePrice}
                          </div>
                        )}
                        <div className="text-xl font-black text-[#0D004C]">
                          ₹{price}
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setSelectedService(service)}
                          className="flex h-10 w-10 items-center justify-center rounded-lg border border-gray-200 text-[#0D004C] shadow-sm transition hover:border-[#0D004C] hover:bg-[#F0F4FF]"
                          title="Quick View Details"
                          aria-label="Quick View Details"
                        >
                          <FaEye size={16} />
                        </button>

                        <button
                          onClick={() => handleAddToCart(service)}
                          className="flex h-10 items-center justify-center gap-1 rounded-lg bg-[#0D004C] px-4 text-xs font-bold text-white shadow-md transition hover:bg-[#16007A] active:scale-95"
                        >
                          + Add
                        </button>
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* QUICK VIEW MODAL */}
      <AnimatePresence>
        {selectedService && (
          <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex justify-center items-center px-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.25 }}
              className="bg-white max-w-4xl w-full rounded-3xl overflow-hidden shadow-2xl flex flex-col md:flex-row relative"
            >
              <button
                onClick={() => setSelectedService(null)}
                className="absolute top-4 right-4 z-10 w-9 h-9 rounded-full bg-white/80 backdrop-blur-md text-gray-500 hover:text-black flex items-center justify-center shadow transition"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>

              {/* IMAGE */}
              <div className="md:w-1/2 h-64 md:h-auto bg-gray-100">
                <img
                  src={
                    selectedService.image?.startsWith("http")
                      ? selectedService.image
                      : `http://skishore.in/api/public/${selectedService.image}`
                  }
                  alt={selectedService.name}
                  className="w-full h-full object-cover"
                />
              </div>

              {/* DETAILS */}
              <div className="md:w-1/2 p-6 sm:p-8 flex flex-col justify-between">
                <div>
                  <h3 className="text-2xl font-bold text-[#0D004C] mb-3">
                    {selectedService.name}
                  </h3>

                  <p className="text-xs sm:text-sm text-gray-600 mb-6 leading-relaxed">
                    {selectedService.description}
                  </p>

                  <div className="text-3xl font-extrabold text-[#0D004C] mb-8">
                    ₹{selectedService.price}
                  </div>
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      handleAddToCart(selectedService);
                      setSelectedService(null);
                    }}
                    className="flex-1 bg-[#0D004C] text-white py-3 rounded-2xl font-bold text-xs hover:bg-[#16007A] transition shadow"
                  >
                    + Add to Cart
                  </button>

                  <button
                    onClick={() => {
                      handleAddToCart(selectedService);
                      setSelectedService(null);
                      navigate("/cart");
                    }}
                    className="flex-1 border border-[#0D004C] text-[#0D004C] py-3 rounded-2xl font-bold text-xs hover:bg-[#F0F4FF] transition"
                  >
                    Buy Now →
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
};

export default MostBook;
